/** Detect the user's IANA timezone via the Intl API */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/** Format an ISO time string to a localized time (e.g., "6:00 PM") */
export function formatTime(isoString: string, timezone: string, locale = 'en-US'): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/** Format an ISO date string to a localized date (e.g., "Monday, February 10") */
export function formatDate(isoString: string, timezone: string, locale = 'en-US'): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });
}

/** Get today's date as YYYY-MM-DD in a given timezone */
export function todayInTimezone(timezone: string): string {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).formatToParts(now);
  const y = parts.find(p => p.type === 'year')!.value;
  const m = parts.find(p => p.type === 'month')!.value;
  const d = parts.find(p => p.type === 'day')!.value;
  return `${y}-${m}-${d}`;
}

/** #947 — strict `YYYY-MM-DD` that is a real calendar date, else null. */
export function parseIsoDate(date: string | undefined | null): { y: number; m: number; d: number } | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!dm) return null;
  const y = +dm[1], m = +dm[2], d = +dm[3];
  const check = new Date(Date.UTC(y, m - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return null;
  return { y, m, d };
}

/** #947 — strict `HH:mm` (hours 00–23, minutes 00–59) as minutes of day, else null. */
export function parseHhmm(hhmm: string | undefined | null): number | null {
  const tm = /^(\d{2}):(\d{2})$/.exec(hhmm ?? '');
  if (!tm) return null;
  const h = +tm[1], mi = +tm[2];
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/**
 * #947 — a venue wall clock (`YYYY-MM-DD` + `HH:mm` + `addMinutes`) as a NAIVE
 * ISO string — `2026-12-04T11:00:00`, no offset. This is what the widget sends
 * for a time it fixes itself: the server anchors a naive slot to the VENUE's
 * zone (#911 — `routes/booking.ts`, `routes/deposits-api.ts`, both ends), so no
 * client-side zone arithmetic (which old browsers can get wrong) is on the
 * booking path. Pure UTC arithmetic — day rollover only. Null when malformed.
 */
export function naiveLocalIso(date: string, hhmm: string, addMinutes = 0): string | null {
  const d = parseIsoDate(date);
  const mins = parseHhmm(hhmm);
  if (!d || mins === null) return null;
  const wall = Date.UTC(d.y, d.m - 1, d.d) + (mins + addMinutes) * 60_000;
  return new Date(wall).toISOString().slice(0, 19);
}

/** Minutes east of UTC for `timeZone` at `epochMs`, or null if Intl cannot say. */
function zoneOffsetMinutes(timeZone: string, epochMs: number): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
      .formatToParts(new Date(epochMs));
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    if (raw === 'GMT' || raw === 'UTC') return 0;
    const m = raw.match(/^GMT([+-])(\d{2}):(\d{2})$/);
    if (!m) return null;
    const mins = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
    return m[1] === '-' ? -mins : mins;
  } catch {
    return null;
  }
}

function formatOffset(mins: number): string {
  const sign = mins < 0 ? '-' : '+';
  const abs = Math.abs(mins);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/**
 * #947 — a NAIVE venue wall clock (`2026-12-04T11:00:00`) anchored to the
 * venue's offset (`2026-12-04T11:00:00+02:00`) for DISPLAY only (the summary
 * formats an instant; a naive string would be read in the guest's own zone).
 * Never used on the wire. Null when malformed or when Intl cannot resolve the
 * offset — callers fall back rather than guess `+00:00`.
 */
export function anchorForDisplay(naive: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(naive || '');
  if (!m) return null;
  const wallAsUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  if (Number.isNaN(wallAsUtc)) return null;
  // Two passes: the offset at the guessed instant, then at the corrected one (DST).
  const first = zoneOffsetMinutes(timeZone, wallAsUtc);
  if (first === null) return null;
  const offset = zoneOffsetMinutes(timeZone, wallAsUtc - first * 60_000);
  if (offset === null) return null;
  return `${naive}${formatOffset(offset)}`;
}

/**
 * #947 — "Fri 4 Dec, 11:00" from a venue-local date + `HH:mm`. Pure string
 * arithmetic (the inputs are already venue-local), so it can never drift by the
 * guest's own timezone.
 */
export function formatFixedWhen(date: string, hhmm: string): string {
  const p = parseIsoDate(date);
  if (!p) return '';
  const d = new Date(Date.UTC(p.y, p.m - 1, p.d));
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  // en-GB yields "Fri 4 Dec" (some ICU builds add a comma after the weekday).
  const clean = day.replace(',', '');
  return hhmm ? `${clean}, ${hhmm}` : clean;
}
